import React from "react";
import { CardContent } from "@/components/ui/card";
import { useDashboardData } from "@/services/dashboardDataService";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import "./widgets.css";
import { cleanDateAndTimeObject } from "@shared/renderDate";

interface TotalRisksData {
  totalRisks: number;
  lastUpdated: {
    toDate: () => Date;
  };
  error?: boolean;
  errorMessage?: string;
}

const TotalRisksCountWidget: React.FC = () => {
  const { data, isLoading, error } = useDashboardData('total-risks-count');

  // Report widget status to dashboard context
  useWidgetStatusReporter('total-risks-count', isLoading, error, !!data);

  // Format the last updated time
  const lastUpdated = data?.lastUpdated 
    ? cleanDateAndTimeObject(data.lastUpdated).toLocaleTimeString() 
    : null;

  // Show loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <Skeleton className="h-6 w-3/4 mb-4" />
        <div className="flex flex-col items-center w-full" style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}>
          <Skeleton className="h-16 w-1/2" style={{ minHeight: '4rem' }} />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </CardContent>
    );
  }

  // Show error state
  if (error || data?.error) {
    return (
      <CardContent className="h-full w-full p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {data?.errorMessage || 'Failed to load risks data. Please try again later.'}
          </AlertDescription>
        </Alert>
      </CardContent>
    );
  }

  // Show data
  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <h3 className="text-lg font-semibold mb-2">Total Risks Count</h3>
      <div
        className="flex flex-col items-center justify-center flex-1 w-full h-full"
        style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}
      >
        <span
          className="font-bold text-primary"
          style={{
            fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
            lineHeight: 1,
            minHeight: '1em',
          }}
        >
          {data?.totalRisks?.toLocaleString() || 0}
        </span>
        <span
          className="text-muted-foreground"
          style={{
            fontSize: 'clamp(1rem, 2.5cqw, 2rem)',
            marginTop: 'clamp(0.25rem, 1cqw, 1rem)',
          }}
        >
          total risks
        </span>
      </div>
      {lastUpdated && (
        <div className="text-xs text-muted-foreground mt-2">
          Updated: {lastUpdated}
        </div>
      )}
    </CardContent>
  );
};

export default TotalRisksCountWidget;
